<template>
  <div class="bg-white rounded-lg pb-5 px-6">
    <div class="flex items-center gap-2 mb-6">
      <div class="w-3 h-3 bg-green-500 rounded-full"></div>
      <h2 class="text-xl font-semibold text-gray-900">
        Upcoming Sessions ({{ totalSessions ?? sessions.length }})
      </h2>
    </div>

    <!-- No sessions message -->
    <div v-if="sessions.length === 0" class="text-center py-12">
      <div class="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
        <CalendarIcon class="w-12 h-12 text-gray-400" />
      </div>
      <h3 class="text-lg font-medium text-gray-900 mb-2">No Upcoming Sessions</h3>
      <p class="text-gray-500 max-w-md mx-auto">
        You don't have any upcoming sessions scheduled. Schedule an appointment with a tutor to get started
        with your learning journey!
      </p>
    </div>

    <!-- Sessions grid -->
    <div v-else class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div
        v-for="session in sessions"
        :key="session.id"
        @click="openSessionDetails(session)"
        :class="[
          'bg-white rounded-lg p-4 sm:p-6 hover:scale-102 transition-all duration-300 cursor-pointer overflow-hidden',
          session.status === 'in-progress'
            ? 'session-card-in-progress'
            : 'border-2 border-blue-800 shadow-[4px_4px_0_0_rgba(30,64,175,1)]',
        ]"
      >
        <!-- Session Header -->
        <div class="mb-4">
          <!-- Desktop: Tutor Info + Status Tags in same row -->
          <div class="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
            <!-- Tutor Info -->
            <div class="flex items-center gap-3">
              <!-- Use UserAvatar for active users -->
              <UserAvatar
                v-if="session.tutorName !== 'Deactivated User'"
                :src="session.tutorProfileImage"
                :name="session.tutorName"
                size="md"
              />
              <!-- Show UserCircleIcon only for deactivated users -->
              <div
                v-else
                class="w-10 h-10 flex-shrink-0 bg-gray-300 rounded-full flex items-center justify-center"
              >
                <UserCircleIcon class="w-10 h-10 text-gray-500" />
              </div>
              <div class="flex-1">
                <h4 class="font-medium text-gray-900">{{ session.tutorName }}</h4>
              </div>
            </div>

            <!-- Status Tags - Desktop: upper right, Mobile: below -->
            <div class="flex items-center gap-2 flex-shrink-0">
              <span
                v-if="session.status === 'in-progress'"
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white border-2 border-blue-800 shadow-lg animated-gradient whitespace-nowrap"
              >
                In Progress
              </span>
              <span
                v-else-if="session.status === 'confirmed'"
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border-2 border-green-800 whitespace-nowrap"
              >
                Confirmed
              </span>
              <span
                v-else
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap"
                :class="getStatusClasses(session.status)"
              >
                Pending
              </span>
              <span
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap"
                :class="getTypeClasses(session.mode)"
              >
                {{ session.mode === "face-to-face" ? "Face-to-Face" : "Online" }}
              </span>
            </div>
          </div>
        </div>

        <!-- Session Details -->
        <div class="space-y-3 mb-4">
          <div
            class="flex justify-between border-2 border-blue-800 bg-blue-50/50 rounded-lg py-2 px-4"
          >
            <div class="flex items-center gap-2 text-sm text-blue-800">
              <CalendarIcon class="w-4 h-4" />
              <span class="font-semibold">{{ session.date }}</span>
            </div>

            <div class="flex items-center gap-2 text-sm text-blue-800">
              <ClockIcon class="w-4 h-4" />
              <span class="font-semibold">{{ session.time }}</span>
            </div>
          </div>

          <div class="flex items-center gap-2 text-sm text-gray-800 px-4">
            <BookOpenIcon class="w-4 h-4" />
            <span class="font-semibold">{{ session.subject }}</span>
          </div>

          <div
            v-if="session.mode === 'online'"
            class="border border-blue-200 bg-blue-50/50 rounded-sm py-2 px-4"
            @click.stop
          >
            <div class="flex items-center gap-2 text-sm text-blue-700 mb-2">
              <VideoCameraIcon class="w-4 h-4" />
              <span class="font-semibold">Meeting Link:</span>
            </div>
            <!-- Show meeting link if within 30 minutes or in-progress -->
            <div
              v-if="
                shouldShowMeetingLink(session) &&
                session.meetingLink &&
                session.meetingLink !== 'Meeting link will be provided after approval.'
              "
              class="flex items-center gap-2"
            >
              <a
                :href="session.meetingLink"
                target="_blank"
                rel="noopener noreferrer"
                class="flex-1 text-sm text-blue-600 hover:text-blue-800 underline truncate"
                :title="session.meetingLink"
                @click.stop
              >
                {{ session.meetingLink }}
              </a>
              <button
                @click.stop="copyMeetingLink(session.meetingLink)"
                class="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded transition-colors cursor-pointer"
                title="Copy link"
              >
                <DocumentDuplicateIcon class="w-4 h-4" />
              </button>
            </div>
            <!-- Show waiting message if not yet time -->
            <div v-else class="text-sm text-gray-600">
              <span v-if="session.status === 'confirmed' || session.status === 'pending'">
                Meeting link will be available 30 minutes before the session.
              </span>
              <span v-else> Meeting link will be provided after approval. </span>
            </div>
          </div>

          <div
            v-if="session.location"
            class="flex items-center border border-orange-200 bg-orange-50/50 rounded-sm py-2 px-4 gap-2 text-sm text-orange-700 min-w-0"
          >
            <MapPinIcon class="w-4 h-4 flex-shrink-0" />
            <span class="truncate" :title="session.location">{{ session.location }}</span>
          </div>

          <div
            v-if="session.notes"
            class="text-sm text-gray-600 border border-gray-200 bg-gray-50/50 rounded-sm py-2 px-4"
          >
            <span class="font-medium">Notes:</span> {{ truncateNotes(session.notes, 100) }}
          </div>

          <!-- Reschedule Status -->
        </div>

        <!-- Action Buttons -->
        <div class="flex gap-2" @click.stop>
          <button
            @click="handleRescheduleClick(session)"
            class="flex-1 px-3 py-2 bg-white border-2 border-orange-800 text-orange-800 rounded-md shadow-[4px_4px_0_0_rgba(194,65,12,1)] hover:bg-orange-500 hover:text-white transition-colors text-sm font-medium flex items-center justify-center gap-2 cursor-pointer"
            :title="getRescheduleButtonInfo(session).tooltip"
          >
            <ArrowPathIcon class="w-4 h-4" />
            {{ getRescheduleButtonInfo(session).text }}
          </button>

          <button
            @click="handleCancelClick(session)"
            class="flex-1 px-3 py-2 bg-white border-2 border-red-800 text-red-800 rounded-md shadow-[4px_4px_0_0_rgba(153,27,27,1)] hover:bg-red-500 hover:text-white transition-colors text-sm font-medium flex items-center justify-center gap-2 cursor-pointer"
            :title="getCancelButtonInfo(session).tooltip"
          >
            <XMarkIcon class="w-4 h-4" />
            {{ getCancelButtonInfo(session).text }}
          </button>
        </div>

        <!-- Message Button -->
        <div class="mt-3" @click.stop>
          <button
            @click="openMessagesView(session)"
            :disabled="isLoadingMessage === session.id"
            class="w-full px-3 py-2 bg-white border-2 border-blue-800 text-blue-800 rounded-md shadow-[4px_4px_0_0_rgba(30,64,175,1)] hover:bg-blue-500 hover:text-white transition-colors text-sm font-medium flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div v-if="isLoadingMessage === session.id" class="animate-spin rounded-full h-4 w-4 border-2 border-blue-800 border-t-transparent"></div>
            <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
            {{ isLoadingMessage === session.id ? 'Loading...' : 'Message' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Session Details Modal -->
    <SessionDetailsModal :isOpen="isModalOpen" :session="selectedSession" @close="closeModal" />

    <!-- Session Toast Container -->
    <SessionToast />
  </div>
</template>
<script setup lang="ts">
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import {
  CalendarIcon,
  ClockIcon,
  BookOpenIcon,
  VideoCameraIcon,
  MapPinIcon,
  ArrowPathIcon,
  XMarkIcon,
  DocumentDuplicateIcon,
  UserCircleIcon,
} from "@heroicons/vue/24/outline";
import type { UpcomingSession } from "@/types";
import { sessionService } from "@/services/sessionService";
import { auth } from "@/firebase";
import UserAvatar from "@/components/UserAvatar.vue";
import {
  canCancelSession as canCancelByTime,
  canRescheduleSession as canRescheduleByTime,
  isSessionWithin12Hours,
  getSessionTimeMessage,
} from "@/utils/sessionTimeUtils";
import SessionDetailsModal from "./SessionDetailsModal.vue";
import { useToast } from "@/composables/useSessionToast";
import SessionToast from "./SessionToast.vue";
import { onMounted, onUnmounted } from "vue";
import { logDebug, logInfo, logError, logSuccess } from "@/utils/secureLogger";

const router = useRouter();

// Toast notification
const { showWarning, showInfo } = useToast();

// Track which sessions have shown the meeting link notification
const notifiedSessions = ref<Set<string>>(new Set());

// Modal state
const isModalOpen = ref(false);
const selectedSession = ref<UpcomingSession | null>(null);

// Message loading state
const isLoadingMessage = ref<string | null>(null); // Store session ID being loaded

const openSessionDetails = (session: UpcomingSession) => {
  logDebug("Opening session details modal", {
    sessionId: session.id,
    tutorId: (session as any).tutorId || "unknown",
    hasMaterials: Boolean((session as any).materials?.length),
  });
  selectedSession.value = session;
  isModalOpen.value = true;
};

const closeModal = () => {
  isModalOpen.value = false;
  selectedSession.value = null;
};

// Truncate notes to a maximum length
const truncateNotes = (notes: string, maxLength: number = 100) => {
  if (!notes) return "";
  if (notes.length <= maxLength) return notes;
  return notes.substring(0, maxLength) + "...";
};

interface Props {
  sessions: UpcomingSession[];
  hideHeader?: boolean;
  totalSessions?: number; // Total count for accurate counter (used when sessions are paginated)
}

const props = withDefaults(defineProps<Props>(), {
  hideHeader: false,
  totalSessions: undefined,
});

const emit = defineEmits<{
  reschedule: [session: UpcomingSession];
  cancel: [session: UpcomingSession];
}>();

// Helper function to check if a session can be rescheduled
const canRescheduleSession = (session: UpcomingSession) => {
  // First check time restrictions
  const sessionStartTime = new Date(
    (session as any).startAt || new Date(session.date + " " + session.time)
  );
  const timeCheck = canRescheduleByTime(sessionStartTime, session.status);

  if (!timeCheck.canReschedule) {
    return {
      canReschedule: false,
      reason: timeCheck.timeRestrictionMessage,
    };
  }

  // Then check reschedule count limits
  const sessionData = {
    ...session,
    sessionStatus: session.status,
    totalRescheduleCount: (session as any).totalRescheduleCount || 0,
    startAt: { toDate: () => sessionStartTime },
  };

  return sessionService.canRescheduleSession(sessionData as any, "student");
};

// Helper function to check if a session can be cancelled
const canCancelSessionWithRestrictions = (session: UpcomingSession) => {
  const sessionStartTime = new Date(
    (session as any).startAt || new Date(session.date + " " + session.time)
  );
  return canCancelByTime(sessionStartTime, session.status);
};

// Helper function to get reschedule button text and tooltip
const getRescheduleButtonInfo = (session: UpcomingSession) => {
  const rescheduleStatus = canRescheduleSession(session);

  if (!rescheduleStatus.canReschedule) {
    return {
      disabled: true,
      text: "Cannot Reschedule",
      tooltip: rescheduleStatus.reason || "Reschedule not available",
    };
  }

  return {
    disabled: false,
    text: "Reschedule",
    tooltip: "Reschedule this session",
  };
};

// Helper function to get cancel button text and tooltip
const getCancelButtonInfo = (session: UpcomingSession) => {
  const cancelStatus = canCancelSessionWithRestrictions(session);

  if (!cancelStatus.canCancel) {
    return {
      disabled: true,
      text: "Cannot Cancel",
      tooltip: cancelStatus.timeRestrictionMessage,
    };
  }

  return {
    disabled: false,
    text: "Cancel",
    tooltip: "Cancel this session",
  };
};

// Click handlers that check time restrictions and show warning modal
const handleRescheduleClick = (session: UpcomingSession) => {
  const sessionStartTime = new Date(
    (session as any).startAt || new Date(session.date + " " + session.time)
  );
  const { isWithin12Hours, hoursRemaining } = isSessionWithin12Hours(sessionStartTime);

  if (isWithin12Hours) {
    showWarning("Cannot Reschedule", getSessionTimeMessage(hoursRemaining));
    return;
  }

  // If not within 4 hours, proceed with normal reschedule logic
  const rescheduleStatus = canRescheduleSession(session);
  if (!rescheduleStatus.canReschedule) {
    showWarning("Cannot Reschedule", rescheduleStatus.reason || "Reschedule not available");
    return;
  }

  emit("reschedule", session);
};

const handleCancelClick = (session: UpcomingSession) => {
  const sessionStartTime = new Date(
    (session as any).startAt || new Date(session.date + " " + session.time)
  );
  const { isWithin12Hours, hoursRemaining } = isSessionWithin12Hours(sessionStartTime);

  if (isWithin12Hours) {
    showWarning("Cannot Cancel", getSessionTimeMessage(hoursRemaining));
    return;
  }

  // If not within 4 hours, proceed with normal cancel logic
  const cancelStatus = canCancelSessionWithRestrictions(session);
  if (!cancelStatus.canCancel) {
    showWarning("Cannot Cancel", cancelStatus.timeRestrictionMessage);
    return;
  }

  emit("cancel", session);
};

// Check if meeting link should be visible (30 minutes before or during session)
const shouldShowMeetingLink = (session: UpcomingSession): boolean => {
  if (session.status === "in-progress") return true;

  const sessionStartTime = new Date(
    (session as any).startAt || new Date(session.date + " " + session.time)
  );
  const now = new Date();
  const minutesUntilSession = (sessionStartTime.getTime() - now.getTime()) / (1000 * 60);

  // Show link if within 30 minutes of start time
  const shouldShow = minutesUntilSession <= 30 && minutesUntilSession > 0;

  return shouldShow;
};

const openMessagesView = async (session: UpcomingSession) => {
  try {
    // Set loading state for this specific session
    isLoadingMessage.value = session.id;
    
    logInfo("Starting conversation from upcoming session", { sessionId: session.id });

    // Import the messaging permission service
    const { messagingPermissionService } = await import("@/services/messagingPermissionService");

    // Get current user
    const currentUser = auth.currentUser;
    if (!currentUser) {
      alert("Please log in to send messages");
      isLoadingMessage.value = null;
      return;
    }

    // Create conversation from session context
    const result = await messagingPermissionService.createConversationFromSession(
      session.id,
      currentUser.uid
    );

    if (!result.success) {
      alert(result.reason || "Unable to start conversation");
      isLoadingMessage.value = null;
      return;
    }

    // Get the tutor's information for CometChat
    const tutorId = (session as any).tutorId || session.tutorName; // Use tutorId if available, fallback to tutorName

    if (!tutorId) {
      alert("Unable to identify the tutor for messaging");
      isLoadingMessage.value = null;
      return;
    }

    // Note: CometChat initialization and user creation will be handled by the messaging view
    // We just create the conversation and navigate

    // Navigate to student messages view with the specific conversation
    router.push({
      path: "/student-messages",
      query: {
        conversation: result.conversationId,
        contact: tutorId,
      },
    });

    logSuccess("Successfully navigated to messaging with tutor", {
      tutorId,
      conversationId: result.conversationId,
    });
    
    // Clear loading state after successful navigation
    isLoadingMessage.value = null;
    
  } catch (error) {
    logError("Error starting conversation from upcoming session", error);
    alert("Failed to start conversation. Please try again.");
    isLoadingMessage.value = null;
  }
};

const copyMeetingLink = async (meetingLink: string) => {
  try {
    await navigator.clipboard.writeText(meetingLink);
    // You could add a toast notification here if you have one
  } catch (err) {
    logError("Failed to copy meeting link", err);
    // Fallback for older browsers
    const textArea = document.createElement("textarea");
    textArea.value = meetingLink;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand("copy");
    document.body.removeChild(textArea);
  }
};

// Set up interval to check for meeting link availability
let checkInterval: ReturnType<typeof setInterval>;

onMounted(() => {
  // Check every minute for meeting link availability
  checkInterval = setInterval(() => {
    props.sessions.forEach((session) => {
      shouldShowMeetingLink(session);
    });
  }, 60000); // 60 seconds
});

onUnmounted(() => {
  if (checkInterval) {
    clearInterval(checkInterval);
  }
});

const getStatusClasses = (status: string) => {
  switch (status) {
    case "confirmed":
      return "bg-green-100 text-green-800 border-2 border-green-800";
    case "pending":
      return "bg-orange-100 text-orange-800 border-2 border-orange-800";
    default:
      return "bg-gray-100 text-gray-800 border-2 border-gray-800";
  }
};

const getTypeClasses = (mode: string) => {
  switch (mode) {
    case "face-to-face":
      return "bg-orange-100 text-orange-800 border-2 border-orange-800";
    case "online":
      return "bg-blue-100 text-blue-800 border-2 border-blue-800";
    default:
      return "bg-gray-100 text-gray-800 border-2 border-gray-800";
  }
};
</script>

<style scoped>
/* Pulsing blue effect for in-progress sessions */
.session-card-in-progress {
  border: 3px solid #1e40af;
  animation: pulse-shadow 2s ease-in-out infinite;
}

@keyframes pulse-shadow {
  0%,
  100% {
    box-shadow: 4px 4px 0 0 rgba(30, 64, 175, 1), 0 0 0 0 rgba(30, 64, 175, 0);
  }
  50% {
    box-shadow: 5px 5px 0 0 rgba(30, 64, 175, 1), 0 0 20px 5px rgba(30, 64, 175, 0.3);
  }
}

/* Animated gradient for in-progress label */
.animated-gradient {
  background: linear-gradient(90deg, #146eff, #5d38ff, #6222eb);
  background-size: 300% 100%;
  animation: gradient-shift 3s ease infinite;
}

@keyframes gradient-shift {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 90% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}
</style>
